/**
 * Storefront-only UI variant per refinement attribute.
 */
import type { ShopperSearch } from '@/scapi';
import type { FilterValue } from '@/components/category-refinements/types';
import { DATE_FACET_ATTRIBUTE_ID } from './date-facet';
import { VENUE_FACET_ATTRIBUTE_ID } from './venue-facet';
export type FilterUiType =
    | 'price-slider'
    | 'skip'
    | 'boolean-toggle'
    | 'searchable-list'
    | 'checkbox-list'
    | 'toggle-list';

const ON_BOOLEAN_TOGGLE_REFINEMENT_VALUE = 'true';

export function inferFilterUiType(r: ShopperSearch.schemas['ProductSearchRefinement']): FilterUiType {
    const values = r.values ?? [];

    // Promoted sticky-bar filters — never render as drawer pills.
    if (r.attributeId === DATE_FACET_ATTRIBUTE_ID || r.attributeId === VENUE_FACET_ATTRIBUTE_ID) {
        return 'skip';
    }

    if (r.attributeId === 'price') {
        const hasExactlyOneValue = values.length === 1;
        const hasValidProducts = (values[0]?.hitCount ?? 0) > 0;
        return hasExactlyOneValue && hasValidProducts ? 'price-slider' : 'skip';
    }
    if (r.attributeId === 'pmid' && values.length > 0) {
        return 'boolean-toggle';
    }

    if (values.length === 0) {
        return 'skip';
    }

    if (values.length === 1 && values[0]?.value === ON_BOOLEAN_TOGGLE_REFINEMENT_VALUE) {
        return 'boolean-toggle';
    }

    if (values.length <= 6) {
        return 'toggle-list';
    }

    if (values.length <= 9) {
        return 'checkbox-list';
    }

    return 'searchable-list';
}

export const isBooleanToggleRefinement = (r: ShopperSearch.schemas['ProductSearchRefinement']): boolean =>
    inferFilterUiType(r) === 'boolean-toggle';

export function toRefinementWithValues(
    refinement: ShopperSearch.schemas['ProductSearchRefinement']
): ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] } {
    return {
        ...refinement,
        values: refinement.values ?? [],
    };
}
